import { useQuery } from '@apollo/client/react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Counter,
  EntryList,
  Flex,
  MAX_FILE_SIZE,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  TextField,
  Uploader,
} from '~/components/blocks';
import { ExternalLink } from '~/components/blocks/ExternalLink';
import { GetOrganizationDocument } from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { editSettingDrawerState } from '~/state/settings_organization_webbookingsettings/atoms';
import { getExtensions } from '~/utils/file';

import { BusinessHoursDisplay } from './BusinessHoursDisplay';
import { Fields } from './types';
import { DESCRIPTION_MAX_LENGTH, OPENINGHOUR_MAX_LENGTH, validationSchema } from './validation';

type Props = {
  editPublished: boolean;
  initialValues: Fields;
  disabled: boolean;
  onSubmit: (values: Fields, formikHelpers: FormikHelpers<Fields>) => void;
  electronicPrescription: boolean;
  hasOrganizationBusinessHours: boolean;
  paymentAvailable: boolean;
};

const EXTENSTIONS = ['image/jpeg', 'image/png'];

const ItemBox = styled(Box)(({ theme }) =>
  css({
    backgroundColor: theme.colors.background.bg,
    marginTop: theme.space.m,
    padding: theme.space.m,
  }),
);

const OnlineQualificationConfirmationUrlTextField = styled(TextField)(({ theme }) =>
  css({
    background: theme.colors.background.default,
  }),
);

export const WebBookingSettingForm = forwardRef<FormikProps<Fields>, Props>((props, ref) => {
  const theme = useTheme();
  const {
    disabled,
    onSubmit,
    electronicPrescription,
    hasOrganizationBusinessHours,
    paymentAvailable,
  } = props;

  const router = useRouter();
  const [businessHoursElement, setBusinessHoursElement] = useState<HTMLElement | null>(null);
  const [drawerState, setDrawerState] = useAtom(editSettingDrawerState);
  const handleClickOrganizationAddressAlert = useCallback(() => {
    router.push('/settings/organization');
  }, [router]);

  const { data: organizationData, loading: isOrganizationLoading } =
    useQuery(GetOrganizationDocument);
  const organizationAddresses = organizationData
    ? getMe(organizationData)?.organization.organizationAddresses
    : null;
  const organizationAddress =
    organizationAddresses && organizationAddresses.length > 0 ? organizationAddresses[0] : null;
  const isVisibleOrganizationAddressAlert = !isOrganizationLoading && !organizationAddress;

  useEffect(() => {
    if (!drawerState.shouldScrollToBusinessHours) return;
    if (!businessHoursElement) return;

    const timer = setTimeout(() => {
      businessHoursElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setDrawerState((state) => ({ ...state, shouldScrollToBusinessHours: false }));
    }, 400);
    return () => clearTimeout(timer);
  }, [businessHoursElement, drawerState.shouldScrollToBusinessHours, setDrawerState]);

  return (
    <Formik
      enableReinitialize
      innerRef={ref}
      initialValues={props.initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <Box padding={theme.space.xl}>
          {props.editPublished && (
            <EntryList>
              <EntryList.Head>店舗公開</EntryList.Head>
              <EntryList.Body>
                {isVisibleOrganizationAddressAlert && (
                  <Alert mb={theme.space.m}>
                    <Text size="s">公開するには「住所」を設定してください</Text>
                    <Button use="default" size="s" onClick={handleClickOrganizationAddressAlert}>
                      <Text size="xs">確認</Text>
                    </Button>
                  </Alert>
                )}
                <Flex justifyContent="space-between" alignItems="center">
                  <RadioGroup>
                    <Radio
                      disabled={isVisibleOrganizationAddressAlert}
                      label="公開"
                      name="published"
                      value="publish"
                      checked={formik.values.published === 'publish'}
                      onChange={formik.handleChange}
                    />
                    <Radio
                      label="非公開"
                      name="published"
                      value="unpublish"
                      checked={formik.values.published === 'unpublish'}
                      onChange={formik.handleChange}
                    />
                  </RadioGroup>
                </Flex>
                <ItemBox>
                  <EntryList>
                    <EntryList.Head>電子処方箋対応</EntryList.Head>
                    <EntryList.Body>{electronicPrescription ? '有効' : '無効'}</EntryList.Body>
                  </EntryList>
                </ItemBox>
                {formik.values.published === 'publish' && (
                  <>
                    <ItemBox>
                      <EntryList>
                        <EntryList.Head>
                          オンライン資格確認対応（オンライン服薬指導）
                        </EntryList.Head>
                        <EntryList.Body>
                          <RadioGroup>
                            <Radio
                              label="有効"
                              name="onlineQualificationConfirmation"
                              value="enabled"
                              disabled={disabled}
                              checked={formik.values.onlineQualificationConfirmation === 'enabled'}
                              onChange={formik.handleChange}
                            />
                            <Radio
                              label="無効"
                              name="onlineQualificationConfirmation"
                              value="disabled"
                              disabled={disabled}
                              checked={formik.values.onlineQualificationConfirmation === 'disabled'}
                              onChange={formik.handleChange}
                            />
                          </RadioGroup>
                        </EntryList.Body>
                      </EntryList>
                      <EntryList>
                        <EntryList.Head>マイナ在宅受付Web URL</EntryList.Head>
                        <EntryList.Body>
                          <OnlineQualificationConfirmationUrlTextField
                            error={formik.errors.onlineQualificationConfirmationUrl}
                            name="onlineQualificationConfirmationUrl"
                            value={formik.values.onlineQualificationConfirmationUrl}
                            onChange={formik.handleChange}
                            placeholder="https://kdg.onshikaku.org/online-care"
                          />
                          <Text color="grey" size="xs" marginTop={theme.space.m}>
                            ※入力したURLでマイナ在宅受付Webにアクセスできるか必ず確認してください
                          </Text>
                        </EntryList.Body>
                      </EntryList>
                    </ItemBox>
                    <ItemBox>
                      {formik.errors.published && (
                        <Alert mb={theme.space.m}>
                          <Text size="s">{formik.errors.published}</Text>
                        </Alert>
                      )}
                      <EntryList>
                        <EntryList.Head>オンライン服薬指導</EntryList.Head>
                        <EntryList.Body>
                          <RadioGroup>
                            <Radio
                              label="有効"
                              name="telemedicine"
                              value="enabled"
                              disabled={disabled || !paymentAvailable}
                              checked={formik.values.telemedicine === 'enabled'}
                              onChange={formik.handleChange}
                            />
                            <Radio
                              label="無効"
                              name="telemedicine"
                              value="disabled"
                              disabled={disabled}
                              checked={formik.values.telemedicine === 'disabled'}
                              onChange={formik.handleChange}
                            />
                          </RadioGroup>
                          {!paymentAvailable && (
                            <Text block color="pink" size="s" marginTop={theme.space.m}>
                              決済代行会社の審査通過後、有効に切り替えることが可能です
                            </Text>
                          )}
                        </EntryList.Body>
                      </EntryList>
                      <EntryList>
                        <EntryList.Head>処方箋ネット受付</EntryList.Head>
                        <EntryList.Body>
                          <RadioGroup>
                            <Radio
                              label="有効"
                              name="facetoface"
                              value="enabled"
                              disabled={disabled}
                              checked={formik.values.facetoface === 'enabled'}
                              onChange={formik.handleChange}
                            />
                            <Radio
                              label="無効"
                              name="facetoface"
                              value="disabled"
                              disabled={disabled}
                              checked={formik.values.facetoface === 'disabled'}
                              onChange={formik.handleChange}
                            />
                          </RadioGroup>
                        </EntryList.Body>
                      </EntryList>
                    </ItemBox>
                  </>
                )}
              </EntryList.Body>
            </EntryList>
          )}
          <EntryList>
            <EntryList.Head>紹介文</EntryList.Head>
            <EntryList.Body>
              {formik.values.published === 'publish' && (
                <Text size="s">
                  紹介文は、患者が薬局を選ぶときの参考になる情報です。
                  <ExternalLink url="https://intercom.help/pharms/ja/articles/5024432-%E7%B4%B9%E4%BB%8B%E6%96%87%E3%82%AC%E3%82%A4%E3%83%89%E3%83%A9%E3%82%A4%E3%83%B3">
                    紹介文ガイドラインを確認
                  </ExternalLink>
                  のうえ、登録してください。
                </Text>
              )}
              <Textarea
                height={140}
                disabled={disabled}
                error={formik.errors.description}
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                placeholder="患者が調剤薬局を検索する際に表示される紹介文を入力してください"
              ></Textarea>
              <Counter value={formik.values.description || ''} max={DESCRIPTION_MAX_LENGTH} />
            </EntryList.Body>
          </EntryList>
          <EntryList>
            <EntryList.Head>ホームページURL</EntryList.Head>
            <EntryList.Body>
              <TextField
                disabled={disabled}
                error={formik.errors.website}
                name="website"
                value={formik.values.website}
                onChange={formik.handleChange}
                placeholder="https://www.medley.jp"
              ></TextField>
            </EntryList.Body>
          </EntryList>
          {formik.values.published === 'publish' && (
            <>
              <EntryList ref={setBusinessHoursElement}>
                <EntryList.Head>営業時間</EntryList.Head>
                <EntryList.Body>
                  <BusinessHoursDisplay
                    formik={formik}
                    disabled={disabled}
                    hasOrganizationBusinessHours={hasOrganizationBusinessHours}
                  />
                </EntryList.Body>
              </EntryList>
              <EntryList>
                <EntryList.Head>営業時間補足</EntryList.Head>
                <EntryList.Body>
                  <Textarea
                    height={140}
                    disabled={disabled}
                    error={formik.errors.openingHours}
                    name="openingHours"
                    value={formik.values.openingHours}
                    onChange={formik.handleChange}
                  />
                  <Counter value={formik.values.openingHours || ''} max={OPENINGHOUR_MAX_LENGTH} />
                </EntryList.Body>
              </EntryList>
            </>
          )}
          <EntryList>
            <EntryList.Head>店舗アイコン</EntryList.Head>
            <EntryList.Body>
              <Uploader
                disabled={disabled}
                accepts={['image/jpeg', 'image/png']}
                value={formik.values.logo}
                placefolder={`${getExtensions(EXTENSTIONS).join(',')}でファイルサイズは「${
                  MAX_FILE_SIZE / 1024 / 1024
                }MB以下」`}
                onChange={(file) => formik.setFieldValue('logo', file)}
              />
            </EntryList.Body>
          </EntryList>
        </Box>
      )}
    </Formik>
  );
});

WebBookingSettingForm.displayName = 'WebBookingSettingForm';
